import React, { useEffect } from 'react';
import { FaTimes, FaCheckCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const ServiceModal = ({ isOpen, onClose, service }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !service) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        animation: 'fadeIn 0.3s ease-out'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'modalSlideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(0, 0, 0, 0.05)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <FaTimes style={{ fontSize: '20px', color: '#333' }} />
        </button>

        {/* Modal Content */}
        <div style={{ padding: '40px 50px' }}>
          {/* Service Icon & Title */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            {service.icon && (
              <div
                style={{
                  fontSize: '56px',
                  marginBottom: '20px',
                  color: '#5749ff'
                }}
              >
                {service.icon}
              </div>
            )}
            <h2
              style={{
                fontSize: '32px',
                fontWeight: '800',
                marginBottom: '12px',
                color: '#2d3748',
                lineHeight: '1.3'
              }}
            >
              {service.title}
            </h2>
            {service.tagline && (
              <p
                style={{
                  fontSize: '18px',
                  color: '#5749ff',
                  fontWeight: '600',
                  marginBottom: '0'
                }}
              >
                {service.tagline}
              </p>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '30px' }}>
            <p
              style={{
                fontSize: '17px',
                lineHeight: '1.8',
                color: '#4a5568',
                marginBottom: '0'
              }}
            >
              {service.subtitle}
            </p>
          </div>

          {/* Capabilities/Features */}
          {service.capabilities && service.capabilities.length > 0 && (
            <div style={{ marginBottom: '35px' }}>
              <h4
                style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '20px',
                  color: '#2d3748'
                }}
              >
                Key Capabilities
              </h4>
              <div style={{ display: 'grid', gap: '12px' }}>
                {service.capabilities.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px 16px',
                      background: 'rgba(87, 73, 255, 0.05)',
                      borderRadius: '10px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaCheckCircle
                      style={{
                        fontSize: '20px',
                        color: '#5749ff',
                        marginTop: '2px',
                        flexShrink: 0
                      }}
                    />
                    <span
                      style={{
                        fontSize: '16px',
                        color: '#2d3748',
                        lineHeight: '1.6'
                      }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA Button */}
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link
              to="/contact-us"
              style={{
                display: 'inline-block',
                background: '#5749ff',
                color: '#fff',
                padding: '16px 40px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(87, 73, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#4636e0';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(87, 73, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#5749ff';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(87, 73, 255, 0.3)';
              }}
            >
              Get Started with This Service
            </Link>
          </div>
        </div>

        {/* Add CSS animations */}
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes modalSlideUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ServiceModal;
